import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArchive, FaUser, FaUserTie, FaComments, FaInfoCircle, FaSpinner } from 'react-icons/fa';

// ===================================================================================
// A. Skeletons for Loading States
// ===================================================================================
const ArchivesSkeleton = () => (
    <div className="animate-pulse p-4 space-y-4">
        {[...Array(8)].map((_, i) => (
            <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
            </div>
        ))}
    </div>
);

// ===================================================================================
// B. Individual Conversation Item in the List
// ===================================================================================
const ConversationItem = ({ log, isSelected, onSelect }) => (
    <div
        onClick={onSelect}
        className={`relative p-4 border-b border-gray-700/50 cursor-pointer transition-colors duration-200 ${
            isSelected ? 'bg-gray-800' : 'hover:bg-gray-800/60'
        }`}
    >
        {isSelected && <div className="absolute left-0 top-0 h-full w-1 bg-cyan-400"></div>}
        <p className="font-semibold text-white truncate">Chat ID: {log.conversationId}</p>
        <p className="text-xs text-gray-400 mt-1">
            {new Date(log.timestamp || Date.now()).toLocaleString()} {/* Assuming timestamp exists */}
        </p>
    </div>
);

// ===================================================================================
// C. The Message Bubble Component
// ===================================================================================
const MessageBubble = ({ msg }) => {
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [msg]);

    // System Event Message
    if (msg.type === 'event') {
        return (
            <div className="my-4 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                <FaInfoCircle />
                <span>
                    <strong>Event:</strong> {msg.event} at {new Date(msg.timestamp).toLocaleString()}
                    {msg.agent && ` by ${msg.agent.name}`}
                </span>
            </div>
        );
    }

    const isAgent = msg.from === 'agent';
    
    // User or Agent Message
    return (
        <div className={`flex items-start gap-3 my-3 ${isAgent ? 'flex-row-reverse' : 'flex-row'}`}>
            <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white ${isAgent ? 'bg-cyan-600' : 'bg-gray-600'}`}>
                {isAgent ? <FaUserTie /> : <FaUser />}
            </div>
            <div className={`p-3 rounded-lg max-w-lg ${isAgent ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-200'}`}>
                <div className="flex items-baseline gap-2">
                    <p className="font-bold text-sm">{isAgent ? msg.agentName || 'Agent' : 'User'}</p>
                    <p className="text-xs opacity-70">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <p className="mt-1 break-words">{msg.text}</p>
            </div>
            <div ref={messagesEndRef} />
        </div>
    );
};

// ===================================================================================
// D. Main Chat History Viewer Component
// ===================================================================================
export function ChatHistoryViewer() {
    const [logs, setLogs] = useState([]);
    const [selectedLogId, setSelectedLogId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    
    useEffect(() => {
        axios.get('/api/chat-history')
            .then(res => setLogs(res.data.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)))) // Sort by most recent
            .catch(err => console.error("Failed to fetch chat logs list", err))
            .finally(() => setTimeout(() => setLoadingLogs(false), 500));
    }, []);

    useEffect(() => {
        if (!selectedLogId) return;

        setLoadingMessages(true);
        setMessages([]); // Clear previous messages
        axios.get(`/api/chat-history/${selectedLogId}`)
            .then(res => setMessages(res.data))
            .catch(err => console.error(`Failed to fetch content for ${selectedLogId}`, err))
            .finally(() => setTimeout(() => setLoadingMessages(false), 300));
    }, [selectedLogId]);
    
    return (
        <div className="flex h-[80vh] bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
            {/* Left Pane: Archives List */}
            <aside className="w-1/3 max-w-sm h-full bg-gray-900/40 flex flex-col flex-shrink-0">
                <div className="p-4 border-b border-gray-700 h-20 flex items-center">
                    <h2 className="text-xl font-bold text-white flex items-center gap-3"><FaArchive /> Chat Archives</h2>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {loadingLogs ? <ArchivesSkeleton /> : 
                        logs.map(log => (
                            <ConversationItem 
                                key={log.conversationId} 
                                log={log} 
                                isSelected={selectedLogId === log.conversationId}
                                onSelect={() => setSelectedLogId(log.conversationId)}
                            />
                        ))
                    }
                </div>
            </aside>

            {/* Right Pane: Message Viewer */}
            <main className="flex-1 h-full flex flex-col">
                <div className="flex-1 p-6 overflow-y-auto">
                    {!selectedLogId ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500 text-center">
                            <FaComments size={48} className="mb-4" />
                            <h3 className="text-xl font-semibold text-gray-400">Select a Conversation</h3>
                            <p>Choose a chat from the archives on the left to view its history.</p>
                        </div>
                    ) : loadingMessages ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <FaSpinner className="animate-spin text-4xl" />
                            <p className="mt-4">Loading Conversation...</p>
                        </div>
                    ) : (
                        messages.map((msg, index) => <MessageBubble key={index} msg={msg} />)
                    )}
                </div>
            </main>
        </div>
    );
}